import React, { useEffect, useRef } from "react";
import { useDragDropManager, useDragLayer } from "react-dnd";

import styles from "./TouchDragPreview.module.css";

/**
 * A copy of the dragged node, without the ids that must stay unique.
 * @param {HTMLElement} node
 * @returns {HTMLElement}
 */
const copyNode = (node) => {
  const copy = /** @type {HTMLElement} */ (node.cloneNode(true));
  for (const element of [copy, ...copy.querySelectorAll("[id]")]) {
    element.removeAttribute("id");
  }
  const { width, height } = node.getBoundingClientRect();
  copy.style.width = `${width}px`;
  copy.style.height = `${height}px`;
  copy.style.margin = "0";
  return copy;
};

/**
 * One drag's ghost, mounted for the length of the drag.
 * @param {{ offset: { x: number, y: number } }} props - Client offset of the
 *   dragged node's top-left corner.
 * @returns {React.ReactElement}
 */
const DragGhost = ({ offset }) => {
  const manager = useDragDropManager();
  const ghostRef = useRef(/** @type {HTMLDivElement?} */ (null));

  // copied once, as the browser snapshots its ghost at drag start. An effect,
  // not a ref: a source that re-renders as its drag starts re-attaches its
  // node during the same commit, so the node is only registered once the
  // commit is done.
  useEffect(() => {
    const sourceId = manager.getMonitor().getSourceId();
    const node = sourceId && manager.getBackend().sourceNodes?.get(sourceId);
    if (node) ghostRef.current.replaceChildren(copyNode(node));
  }, [manager]);

  return (
    <div
      ref={ghostRef}
      data-testid="touch-drag-preview"
      className={styles.preview}
      style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
    />
  );
};

/**
 * The drag ghost for the touch backend, which draws none: a translucent copy
 * of the dragged node that follows the finger, as the browser's ghost does
 * for the HTML5 backend. Mount it once inside a DndProvider using the touch
 * backend.
 * @returns {React.ReactElement | null}
 */
const TouchDragPreview = () => {
  const { isDragging, offset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    offset: monitor.getSourceClientOffset(),
  }));

  return isDragging && offset ? <DragGhost offset={offset} /> : null;
};

export default TouchDragPreview;
